import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {finalize, forkJoin, Observable, of, Subscription, switchMap, tap} from "rxjs";
import {ApplicationInstance} from "../../../../../../services/model/new-application.model";
import {
	FunctionalCapacitiesService,
	FunctionalCapacity, FunctionalCapacityForm
} from "../../../../../../services/back/functional-capacities.service";
import {
	ApplicationDetailData,
	ApplicationDetailService
} from "../../../../../../services/front/application-detail.service";
import {TenantService} from "../../../../../../services/tenant.service";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {ButtonWidgetComponent} from "../../../../../design-system/button-widget/button-widget.component";
import {NgForOf, NgIf} from "@angular/common";
import {MatDialog} from "@angular/material/dialog";
import {FunctionalCapacitiesPopupComponent} from "./functional-capacities-popup/functional-capacities-popup.component";
import {filter} from "rxjs/operators";
import {DesignSystemModule} from "../../../../../design-system/design-system.module";
import {SnackbarService} from "../../../../../../services/front/snackbar.service";

@Component({
  selector: 'app-application-functional-capacities',
	imports: [
		ButtonWidgetComponent,
		NgIf,
		TranslateModule,
		DesignSystemModule,
		NgForOf
	],
  templateUrl: './application-functional-capacities.component.html',
  styleUrl: './application-functional-capacities.component.scss'
})
export class ApplicationFunctionalCapacitiesComponent implements OnInit, OnDestroy {
	subscription: Subscription = new Subscription();

	application: ApplicationInstance;
	isEditor: boolean;
	tenantId: string;

	applicationFunctionalCapacities: FunctionalCapacity[] = [];

	initialized: boolean = false;
	_loading: boolean;

	private applicationDetailService = inject(ApplicationDetailService);
	private functionalCapacitiesService = inject(FunctionalCapacitiesService);
	private tenantService: TenantService = inject(TenantService);
	private translateService = inject(TranslateService);
	private dialog = inject(MatDialog);
	private snackbarService = inject(SnackbarService);

	ngOnInit() {
		this.subscription.add(this.applicationDetailService.getApplicationDetailDataChanges()
			.pipe(tap(data => this.setApplicationDetailData(data)))
			.subscribe(() => this.initialize()));
	}

	private setApplicationDetailData(data: ApplicationDetailData): void {
		this.tenantId = data.tenantId;
		this.application = data.instance;
		this.isEditor = data.isEditor;
	}

	initialize(): void {
		this.switchLoading()
			.pipe(
				switchMap(() => this.functionalCapacitiesService.getTenantFunctionalCapacitiesByApplicationId(this.tenantId, this.application.applicationId)),
				tap(applicationFunctionalCapacities => this.setData(applicationFunctionalCapacities)),
				finalize(() => this.switchLoading())
			).subscribe(() => this.initialized = true);
	}

	setData(applicationFunctionalCapacities: FunctionalCapacity[]): void {
		this.applicationFunctionalCapacities = applicationFunctionalCapacities;
	}

	openDialog(): void {
		this.dialog.open(FunctionalCapacitiesPopupComponent, {
			position: { right: '162.5px' },
			width: '475px'
		})
			.afterClosed()
			.pipe(
				filter(result => !!result),
				tap(() => this.applicationDetailService.refreshApplicationInstance())
			).subscribe();
	}

	updateCapacities(removedCapacity: FunctionalCapacity): void {
		const form: FunctionalCapacityForm = {
			functionalCapacityIds: this.applicationFunctionalCapacities.filter(it => it.id !== removedCapacity.id).map(it => it.id),
		}

		this.subscription.add(this.switchLoading()
			.pipe(
				switchMap(() => this.functionalCapacitiesService.updateApplicationFunctionalCapacityList(this.tenantId, this.application.applicationId, form)),
				tap((result) => this.displayUpdatedSucceed(result)),
				finalize(() => this.switchLoading())
			).subscribe(() => this.applicationFunctionalCapacities = this.applicationFunctionalCapacities.filter(it => it.id !== removedCapacity.id)));
	}

	displayUpdatedSucceed(updateSucceed: boolean): void {
		if (updateSucceed) {
			this.snackbarService.show(this.translateService.instant('page.application.detail.update.success'));
		} else {
			this.snackbarService.show(this.translateService.instant('page.application.detail.update.fail'), 'danger-snack');
		}
	}

	switchLoading(): Observable<{}> {
		this._loading = !this._loading;
		return of({})
	}

	ngOnDestroy() {
		this.subscription.unsubscribe();
	}
}
